import React, { FC, useCallback, useState } from 'react';
import { ChevronRight, MoreVert, Sync, LinkOff } from '@mui/icons-material';
import { CircularProgress, Divider, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { TodoIntegrationType } from '@demind-inc/core';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import {
  AVAILABLE_TODO_INTEGRATIONS_TYPE,
  CannyPostOptionnName,
  translateMinToHHhMMmString,
  useAuthContext,
  visibleFeedbackDialogPostNameAtom,
} from '../../../../../data-access';
import { useRemoveTodoIntegration } from '../../../../../data-access/mutations';
import { DurationPickerModal } from '../../../../Scheduler/common';
import { TODO_INTEGRATION_ICONS } from '../../../../TodoIntegration/constants';
import { useConnectAppleReminders, useStartTodoAuth, useTaskSettings } from '../../../../../hooks';
import { useFeatureFlag } from '../../../../../utils';
import { isAppleRemindersDesktopSupported } from '../../../../../helpers';
import { ReactComponent as TodoSectionIcon } from '../../../../../assets/tabs/settings-tab/todo-icon.svg';
import { ReactComponent as PreferencesSectionIcon } from '../../../../../assets/tabs/settings-tab/clock-icon.svg';
import './TodosSection.scss';

const ORDERED_ALL_TODO_INTEGRATIONS: TodoIntegrationType[] = [
  'trello',
  'todoist',
  'ticktick',
  'google-tasks',
  'clickup',
  'github',
  'apple-reminders',
  'notion',
];

const TODO_TYPE_LABELS: Record<TodoIntegrationType, string> = {
  trello: 'Trello',
  todoist: 'Todoist',
  ticktick: 'TickTick',
  'google-tasks': 'Google Tasks',
  clickup: 'ClickUp',
  github: 'GitHub',
  'apple-reminders': 'Apple Reminders',
  notion: 'Notion',
};

const TodosSection: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { connectedTodos, generatingTodoAuth, handleOpenTodoAuth } = useStartTodoAuth({});
  const { removeTodoIntegration, isPending: isRemovingTodoIntegration } =
    useRemoveTodoIntegration();
  const [disconnectingTodoType, setDisconnectingTodoType] = useState<
    TodoIntegrationType | undefined
  >();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuTodoType, setMenuTodoType] = useState<TodoIntegrationType | null>(null);
  const { featureFlags } = useFeatureFlag();
  const [, setVisibleFeedbackPostName] = useRecoilState(visibleFeedbackDialogPostNameAtom);
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
  const { connectAppleReminders, isLoading: isConnectingAppleReminders } =
    useConnectAppleReminders();
  const isAppleRemindersSupported = isAppleRemindersDesktopSupported();

  const {
    taskSettings: { durationMin },
    handleTaskSettingsChange,
    isPending: isUpdatingTaskDuration,
  } = useTaskSettings();

  const isComingSoon = useCallback(
    (todoType: TodoIntegrationType) =>
      !AVAILABLE_TODO_INTEGRATIONS_TYPE.includes(todoType) ||
      (todoType === 'notion' && !featureFlags.FF_notion_integ) ||
      (todoType === 'github' && !featureFlags.FF_github_integrations) ||
      (todoType === 'google-tasks' && !featureFlags.FF_google_tasks),
    [
      featureFlags.FF_github_integrations,
      featureFlags.FF_google_tasks,
      featureFlags.FF_notion_integ,
    ]
  );

  const isBetaIntegration = useCallback(
    (todoType: TodoIntegrationType) => todoType === 'google-tasks' || todoType === 'clickup',
    []
  );

  const handleConnect = useCallback(
    async (todoType: TodoIntegrationType) => {
      if (isComingSoon(todoType)) {
        const postName = {
          todoist: null,
          trello: null,
          ticktick: null,
          clickup: null,
          notion: 'support_notion',
          github: null,
          'google-tasks': null,
          'apple-reminders': null,
        }[todoType];

        setVisibleFeedbackPostName(postName as CannyPostOptionnName | null);
        return;
      }

      if (todoType === 'apple-reminders' && isAppleRemindersSupported) {
        await connectAppleReminders();
        return;
      }

      if (!generatingTodoAuth) {
        handleOpenTodoAuth(todoType);
      }
    },
    [
      generatingTodoAuth,
      handleOpenTodoAuth,
      isComingSoon,
      setVisibleFeedbackPostName,
      isAppleRemindersSupported,
      connectAppleReminders,
    ]
  );

  const handleDisconnect = useCallback(
    async (todoType: TodoIntegrationType) => {
      const integration = connectedTodos.find(({ source }) => source === todoType);
      if (!integration) {
        return;
      }

      setDisconnectingTodoType(todoType);

      try {
        await removeTodoIntegration({
          userId: user.userId,
          todoIntegrationId: integration.todoIntegrationId,
        });
      } finally {
        setDisconnectingTodoType(undefined);
      }
    },
    [connectedTodos, removeTodoIntegration, user.userId]
  );

  const handleSaveTaskDuration = useCallback(
    (hours: number, minutes: number) => {
      const newDurationMin = hours * 60 + minutes;
      handleTaskSettingsChange({ durationMin: newDurationMin });
    },
    [handleTaskSettingsChange]
  );


  const canReconnectTodoType = useCallback(
    (todoType: TodoIntegrationType) =>
      todoType !== 'apple-reminders' || isAppleRemindersSupported,
    [isAppleRemindersSupported]
  );

  const getReconnectLabel = useCallback(
    (todoType: TodoIntegrationType) =>
      todoType === 'apple-reminders'
        ? t('task.todoIntegration.refresh')
        : t('settings.common.reconnect'),
    [t]
  );

  return (
    <Stack gap={2} className="todos-section">
      <Typography variant="h3" className="todos-section__title">
        {t('settings.nav.todos')}
      </Typography>

      <div className="todos-section__block">
        <Stack className="todos-section__groups" gap={2}>
          <Stack className="todos-section__group" gap={1}>
            <Stack
              gap={1}
              direction="row"
              alignItems="center"
              className="todos-section__group-header"
            >
              <TodoSectionIcon />
              <Typography variant="body1" className="todos-section__group-title">
                {t('settings.todos.integrations')}
              </Typography>
            </Stack>

            <Stack className="todos-section__group-card">
              <Stack className="todos-section__list" gap={2}>
                {ORDERED_ALL_TODO_INTEGRATIONS.map((todoType, index) => {
                  const connected = connectedTodos.some(({ source }) => source === todoType);
                  const appleRemindersOnly =
                    todoType === 'apple-reminders' && !isAppleRemindersSupported;
                  const isConnecting =
                    generatingTodoAuth === todoType ||
                    (todoType === 'apple-reminders' && isConnectingAppleReminders);
                  const isDisconnecting = disconnectingTodoType === todoType;
                  const comingSoon = isComingSoon(todoType);

                  const rowButton = connected ? (
                    <Stack direction="row" alignItems="center" className="todos-section__connected-actions">
                      <span className="todos-section__action-button todos-section__action-button--connected">
                        <Typography variant="body2" className="todos-section__action-label">
                          {t('settings.common.connected')}
                        </Typography>
                      </span>
                      {isConnecting || isDisconnecting ? (
                        <CircularProgress size={18} />
                      ) : (
                        <button
                          type="button"
                          className="todos-section__more-button"
                          aria-label={t('task.todoIntegration.moreActions', {
                            source: TODO_TYPE_LABELS[todoType],
                          })}
                          onClick={(e) => {
                            setMenuAnchorEl(e.currentTarget);
                            setMenuTodoType(todoType);
                          }}
                        >
                          <MoreVert className="todos-section__more-icon" />
                        </button>
                      )}
                    </Stack>
                  ) : (
                    <button
                      type="button"
                      className={clsx(
                        'todos-section__action-button',
                        comingSoon
                          ? 'todos-section__action-button--coming-soon'
                          : 'todos-section__action-button--connect'
                      )}
                      onClick={() => handleConnect(todoType)}
                      disabled={appleRemindersOnly || isConnecting}
                    >
                      {isConnecting ? (
                        <CircularProgress size={15} className="todos-section__loading" />
                      ) : (
                        <>
                          <Typography variant="body2" className="todos-section__action-label">
                            {comingSoon
                              ? t('settings.common.comingSoon')
                              : t('settings.common.connect')}
                          </Typography>
                          {isBetaIntegration(todoType) && (
                            <span className="todos-section__beta">{t('settings.common.beta')}</span>
                          )}
                        </>
                      )}
                    </button>
                  );

                  return (
                    <Stack key={todoType} className="todos-section__list-item-wrapper" gap={2}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        className="todos-section__list-item"
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <img
                            src={TODO_INTEGRATION_ICONS[todoType]}
                            alt={TODO_TYPE_LABELS[todoType]}
                            className="todos-section__list-item-icon"
                          />
                          <Typography variant="body1" className="todos-section__list-item-title">
                            {TODO_TYPE_LABELS[todoType]}
                          </Typography>
                        </Stack>

                        {appleRemindersOnly ? (
                          <Tooltip title={t('settings.todos.appleRemindersTooltip')} arrow>
                            <span>{rowButton}</span>
                          </Tooltip>
                        ) : (
                          rowButton
                        )}
                      </Stack>

                      {index !== ORDERED_ALL_TODO_INTEGRATIONS.length - 1 && (
                        <Divider className="todos-section__list-item-divider" />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>

          <Stack className="todos-section__group" gap={1}>
            <Stack
              gap={1}
              direction="row"
              alignItems="center"
              className="todos-section__group-header"
            >
              <PreferencesSectionIcon />
              <Typography variant="body1" className="todos-section__group-title">
                {t('settings.common.preferences')}
              </Typography>
            </Stack>

            <Stack className="todos-section__group-card">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className="todos-section__list-item todos-section__list-item--duration"
                onClick={() => setIsDurationModalOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsDurationModalOpen(true);
                  }
                }}
              >
                <Typography variant="body1" className="todos-section__list-item-title">
                  {t('settings.todos.taskDuration')}
                </Typography>

                {isUpdatingTaskDuration ? (
                  <CircularProgress size={16} />
                ) : (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle1" className="todos-section__duration-value">
                      {translateMinToHHhMMmString(durationMin)}
                    </Typography>
                    <ChevronRight className="todos-section__duration-icon" />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        className="todos-section__more-menu"
      >
        {menuTodoType && canReconnectTodoType(menuTodoType) && (
          <MenuItem
            className="todos-section__more-menu-item"
            disabled={isRemovingTodoIntegration}
            onClick={() => {
              setMenuAnchorEl(null);
              if (menuTodoType) {
                handleConnect(menuTodoType);
              }
            }}
          >
            <Sync fontSize="small" />
            <p className="todos-section__more-menu-item__title">
              {getReconnectLabel(menuTodoType)}
            </p>
          </MenuItem>
        )}
        <MenuItem
          className="todos-section__more-menu-item todos-section__more-menu-item--disconnect"
          disabled={isRemovingTodoIntegration}
          onClick={() => {
            setMenuAnchorEl(null);
            if (menuTodoType) {
              handleDisconnect(menuTodoType);
            }
          }}
        >
          <LinkOff fontSize="small" />
          <p className="todos-section__more-menu-item__title">{t('settings.common.disconnect')}</p>
        </MenuItem>
      </Menu>

      <DurationPickerModal
        isOpen={isDurationModalOpen}
        onClose={() => setIsDurationModalOpen(false)}
        onSave={handleSaveTaskDuration}
        title={t('settings.todos.setDefaultTaskDuration')}
        initialDurationMin={durationMin}
      />
    </Stack>
  );
};

export default TodosSection;
